/**
 * Rekognition Test Component
 * 
 * This component allows testing AWS Rekognition face detection
 * with sample images. It can be accessed at /test-rekognition route.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { detectFacesWithRekognition, validateScanPhotos } from '../services/rekognitionService';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { theme } from '../styles/theme';
import { Loader2, CheckCircle, XCircle, Info, Upload } from 'lucide-react';

const TestContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: ${theme.colors.background.primary};
  min-height: calc(100vh - 72px);
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.base};
`;

const TestSection = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid ${theme.colors.border};
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const UploadArea = styled.div`
  border: 2px dashed ${theme.colors.border};
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.background.primary};
  }
`;

const ImagePreview = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ImageCard = styled.div`
  flex: 1;
  min-width: 200px;
  background: ${theme.colors.background.primary};
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid ${theme.colors.border};
`;

const ImagePreviewImg = styled.img`
  width: 100%;
  height: auto;
  border-radius: 4px;
  margin-bottom: 0.5rem;
`;

const ImageLabel = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const ResultsContainer = styled.div`
  margin-top: 1.5rem;
`;

const ResultCard = styled.div<{ isValid?: boolean }>`
  background: ${props => props.isValid 
    ? 'rgba(34, 197, 94, 0.1)' 
    : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid ${props => props.isValid 
    ? 'rgba(34, 197, 94, 0.3)' 
    : 'rgba(239, 68, 68, 0.3)'};
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const MetadataCard = styled.div`
  background: ${theme.colors.background.primary};
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid ${theme.colors.border};
`;

const MetadataLabel = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const MetadataValue = styled.div`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.status.error};
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  padding: 1rem;
  margin-top: 1rem;
`;

const InfoBox = styled.div`
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: start;
  gap: 0.75rem;
`;

const LoadingSpinner = styled(Loader2)`
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const RekognitionTest: React.FC = () => {
  const { user, getCurrentSession } = useAWSAuth();
  const { showToast } = useToast();
  
  const [frontImage, setFrontImage] = useState<File | null>(null);
  const [sideImage, setSideImage] = useState<File | null>(null);
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [sidePreview, setSidePreview] = useState<string | null>(null);
  const [frontResult, setFrontResult] = useState<any>(null);
  const [sideResult, setSideResult] = useState<any>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'front' | 'side'
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showToast('Please upload an image file', 'error');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      showToast('Image size must be less than 15MB', 'error');
      return;
    }

    if (type === 'front') {
      setFrontImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFrontPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setSideImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSidePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const testSingleImage = async (type: 'front' | 'side') => {
    if (!user) {
      showToast('Please sign in to test Rekognition', 'error');
      return;
    }

    const image = type === 'front' ? frontImage : sideImage;
    if (!image) {
      showToast(`Please upload a ${type} image first`, 'error');
      return;
    }

    setIsLoading(true);
    try {
      const session = await getCurrentSession();
      if (!session) {
        showToast('Unable to get authentication session', 'error');
        setIsLoading(false);
        return;
      }

      const result = await detectFacesWithRekognition(
        session,
        image,
        type
      );

      if (type === 'front') {
        setFrontResult(result);
      } else {
        setSideResult(result);
      }

      if (result.isValid) {
        showToast(`${type.toUpperCase()} image validated successfully!`, 'success');
      } else {
        showToast(result.error || 'Validation failed', 'error');
      }
    } catch (error: any) {
      showToast(`Error: ${error.message}`, 'error');
      if (type === 'front') {
        setFrontResult({ isValid: false, error: error.message });
      } else {
        setSideResult({ isValid: false, error: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const testBothImages = async () => {
    if (!user) {
      showToast('Please sign in to test Rekognition', 'error');
      return;
    }

    if (!frontImage || !sideImage) {
      showToast('Please upload both front and side images', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const session = await getCurrentSession();
      if (!session) {
        showToast('Unable to get authentication session', 'error');
        setIsLoading(false);
        return;
      }

      const result = await validateScanPhotos(
        session,
        frontImage,
        sideImage
      );

      setValidationResult(result);

      if (result.isValid) {
        showToast('Both images validated successfully!', 'success');
      } else {
        showToast('Validation failed. Check errors below.', 'error');
      }
    } catch (error: any) {
      showToast(`Error: ${error.message}`, 'error');
      setValidationResult({
        isValid: false,
        errors: [error.message],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatMetadata = (face: any) => {
    if (!face) return null;

    return (
      <MetadataGrid>
        {face.ageRange && (
          <MetadataCard>
            <MetadataLabel>Age Range</MetadataLabel>
            <MetadataValue>
              {face.ageRange.low} - {face.ageRange.high} years
            </MetadataValue>
          </MetadataCard>
        )}
        {face.gender && (
          <MetadataCard>
            <MetadataLabel>Gender</MetadataLabel>
            <MetadataValue>
              {face.gender.value} ({Math.round(face.gender.confidence)}% confidence)
            </MetadataValue>
          </MetadataCard>
        )}
        {face.emotions && face.emotions.length > 0 && (
          <MetadataCard>
            <MetadataLabel>Primary Emotion</MetadataLabel>
            <MetadataValue>
              {face.emotions[0].type} ({Math.round(face.emotions[0].confidence)}% confidence)
            </MetadataValue>
          </MetadataCard>
        )}
        {face.confidence && (
          <MetadataCard>
            <MetadataLabel>Face Confidence</MetadataLabel>
            <MetadataValue>
              {Math.round(face.confidence)}%
            </MetadataValue>
          </MetadataCard>
        )}
        {face.boundingBox && (
          <MetadataCard>
            <MetadataLabel>Face Size</MetadataLabel>
            <MetadataValue>
              {Math.round(face.boundingBox.width * 100)}% × {Math.round(face.boundingBox.height * 100)}%
            </MetadataValue>
          </MetadataCard>
        )}
        {face.quality && (
          <>
            {face.quality.brightness !== undefined && (
              <MetadataCard>
                <MetadataLabel>Brightness</MetadataLabel>
                <MetadataValue>
                  {Math.round(face.quality.brightness)} / 100
                </MetadataValue>
              </MetadataCard>
            )}
            {face.quality.sharpness !== undefined && (
              <MetadataCard>
                <MetadataLabel>Sharpness</MetadataLabel>
                <MetadataValue>
                  {Math.round(face.quality.sharpness)} / 100
                </MetadataValue>
              </MetadataCard>
            )}
          </>
        )}
      </MetadataGrid>
    );
  };

  return (
    <TestContainer>
      <Header>
        <Title>AWS Rekognition Test</Title>
        <Subtitle>
          Test face detection and metadata extraction with sample images
        </Subtitle>
      </Header>

      {!user && (
        <InfoBox>
          <Info size={20} />
          <div>
            <strong>Sign in required:</strong> Please sign in to test AWS Rekognition.
            The service requires authenticated AWS credentials.
          </div>
        </InfoBox>
      )}

      <TestSection>
        <SectionTitle>Upload Test Images</SectionTitle>
        
        <ImagePreview>
          <ImageCard>
            <ImageLabel>Front Photo</ImageLabel>
            <UploadArea>
              {frontPreview ? (
                <ImagePreviewImg src={frontPreview} alt="Front preview" />
              ) : (
                <>
                  <Upload size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <div>Click to upload front photo</div>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'front')}
                style={{ display: 'none' }}
                id="front-upload"
              />
              <label htmlFor="front-upload">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => document.getElementById('front-upload')?.click()}
                >
                  {frontPreview ? 'Change Image' : 'Upload Front Photo'}
                </Button>
              </label>
            </UploadArea>
            {frontImage && (
              <Button
                variant="primary"
                onClick={() => testSingleImage('front')}
                disabled={isLoading || !user}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                {isLoading ? <LoadingSpinner size={16} /> : 'Test Front Image'}
              </Button>
            )}
          </ImageCard>

          <ImageCard>
            <ImageLabel>Side Photo</ImageLabel>
            <UploadArea>
              {sidePreview ? (
                <ImagePreviewImg src={sidePreview} alt="Side preview" />
              ) : (
                <>
                  <Upload size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                  <div>Click to upload side photo</div>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'side')}
                style={{ display: 'none' }}
                id="side-upload"
              />
              <label htmlFor="side-upload">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => document.getElementById('side-upload')?.click()}
                >
                  {sidePreview ? 'Change Image' : 'Upload Side Photo'}
                </Button>
              </label>
            </UploadArea>
            {sideImage && (
              <Button
                variant="primary"
                onClick={() => testSingleImage('side')}
                disabled={isLoading || !user}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                {isLoading ? <LoadingSpinner size={16} /> : 'Test Side Image'}
              </Button>
            )}
          </ImageCard>
        </ImagePreview>

        {frontImage && sideImage && (
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <Button
              variant="primary"
              onClick={testBothImages}
              disabled={isLoading || !user}
              size="lg"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size={20} style={{ marginRight: '0.5rem' }} />
                  Testing Both Images...
                </>
              ) : (
                'Test Both Images Together'
              )}
            </Button>
          </div>
        )}
      </TestSection>

      {frontResult && (
        <TestSection>
          <SectionTitle>Front Image Results</SectionTitle>
          <ResultCard isValid={frontResult.isValid}>
            <ResultHeader>
              {frontResult.isValid ? (
                <>
                  <CheckCircle size={20} color="#22c55e" />
                  Validation Successful
                </>
              ) : (
                <>
                  <XCircle size={20} color="#ef4444" />
                  Validation Failed
                </>
              )}
            </ResultHeader>
            <div>
              <strong>Face Count:</strong> {frontResult.faceCount}
            </div>
            {frontResult.error && (
              <ErrorMessage>{frontResult.error}</ErrorMessage>
            )}
            {frontResult.faces && frontResult.faces.length > 0 && (
              <div>
                <strong style={{ display: 'block', marginTop: '1rem', marginBottom: '0.5rem' }}>
                  Detected Face Metadata:
                </strong>
                {formatMetadata(frontResult.faces[0])}
              </div>
            )}
          </ResultCard>
        </TestSection>
      )}

      {sideResult && (
        <TestSection>
          <SectionTitle>Side Image Results</SectionTitle>
          <ResultCard isValid={sideResult.isValid}>
            <ResultHeader>
              {sideResult.isValid ? (
                <>
                  <CheckCircle size={20} color="#22c55e" />
                  Validation Successful
                </>
              ) : (
                <>
                  <XCircle size={20} color="#ef4444" />
                  Validation Failed
                </>
              )}
            </ResultHeader>
            <div>
              <strong>Face Count:</strong> {sideResult.faceCount}
            </div>
            {sideResult.error && (
              <ErrorMessage>{sideResult.error}</ErrorMessage>
            )}
            {sideResult.faces && sideResult.faces.length > 0 && (
              <div>
                <strong style={{ display: 'block', marginTop: '1rem', marginBottom: '0.5rem' }}>
                  Detected Face Metadata:
                </strong>
                {formatMetadata(sideResult.faces[0])}
              </div>
            )}
          </ResultCard>
        </TestSection>
      )}

      {validationResult && (
        <TestSection>
          <SectionTitle>Combined Validation Results</SectionTitle>
          <ResultCard isValid={validationResult.isValid}>
            <ResultHeader>
              {validationResult.isValid ? (
                <>
                  <CheckCircle size={20} color="#22c55e" />
                  Both Images Validated Successfully
                </>
              ) : (
                <>
                  <XCircle size={20} color="#ef4444" />
                  Validation Failed
                </>
              )}
            </ResultHeader>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Front Photo:</strong> {validationResult.front.isValid ? '✓ Valid' : '✗ Invalid'}
              <br />
              <strong>Side Photo:</strong> {validationResult.side.isValid ? '✓ Valid' : '✗ Invalid'}
            </div>
            {validationResult.errors && validationResult.errors.length > 0 && (
              <ErrorMessage>
                <strong>Errors:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  {validationResult.errors.map((error: string, index: number) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </ErrorMessage>
            )}
            {validationResult.front.faces && validationResult.front.faces.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <strong>Front Face Metadata:</strong>
                {formatMetadata(validationResult.front.faces[0])}
              </div>
            )}
            {validationResult.side.faces && validationResult.side.faces.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <strong>Side Face Metadata:</strong>
                {formatMetadata(validationResult.side.faces[0])}
              </div>
            )}
          </ResultCard>
        </TestSection>
      )}
    </TestContainer>
  );
};

export default RekognitionTest;

